import React, { useEffect, useState } from 'react';
import { CameraBlue, Peoples, AddUser, CutBox } from '../../ui/assets/svgIcons/icon'
import { Bar, BarChart, Brush, CartesianGrid, Cell, Label, Legend, Line, LineChart, Pie, PieChart, Rectangle, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { Loader, Select } from '@ohif/ui';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getPatientAnalytics from '@custom/rtk-apis/analytics/getPatientAnalytics';
import getReportAnalytics from '@custom/rtk-apis/analytics/getReportAnalytics';
import getAppoitmentAnalytics from '@custom/rtk-apis/analytics/getAppoitmentAnalytics';
import getTurnAroundTime from '@custom/rtk-apis/analytics/getTurnAroundTime';
import getPeerReview from '@custom/rtk-apis/analytics/getPeerReview';
import getRollbackErrorTracking from '@custom/rtk-apis/analytics/getRollbackErrorTracking';
import getRadiologistPerformance from '@custom/rtk-apis/analytics/getRadiologistPerformance';
import gatCalcAvgReportTime from '@custom/rtk-apis/analytics/getCalcAvgReportTime';
import getStratBussiMatrix from '@custom/rtk-apis/analytics/getStratBussiMetrix';
import getRadiologistCaseRatio from '@custom/rtk-apis/analytics/getRadiologistCaseRatio';

const CustomTooltipTAT = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { averageTAT, modality, count } = payload[0].payload;
    return (
      <div className="" style={{ background: '#fff', border: '1px solid #ccc', padding: '10px' }}>
        <p><strong>Modality:</strong> {modality}</p>
        <p><strong>Average TAT:</strong> {averageTAT}</p>
        <p><strong>Count:</strong> {count}</p>
      </div>
    );
  }
  return null;
};


const AdminGraph = ({ selectedYear }) => {

  const COLORS = ['#0f8491', '#0ea7a2', '#caffff', '#f2ffff', '#ffffff'];


  const dispatch = useDispatch<AppDispatch>();
  const patientAnalyticsResponse = useSelector((state: RootState) => state.getPatientAnalyticsResponse);
  const reportAnalyticsResponse = useSelector((state: RootState) => state.getReportAnalyticsResponse);
  const stractNBussiMetrixResponse = useSelector((state: RootState) => state.getStratBussiMetrixResponse);
  const [_modalityOptions, setModalityOpton] = useState(null);
  const [isloading, setIsloading] = useState(false);
  const [modality, setModality] = useState({
    reports: '',
    appointments: ''
  });
  const [graphData, setGraphData] = useState({
    patientGraphData: [],
    reportGraphData: [],
    appointGraphData: [],
    genderGraphData: [],
    turnAroundTimeData: [],
    peerReviewData: [],
    rollbackStatusCounts: [],
    rollBackReasonCounts: [],
    radiologistPerformance: [],
    calculateAverageReportingTime: [],
    stratBussMetrix: [],
    radioCaseRatio: [],
  });
  const [graphType, setGraphType] = useState({
    patient: { type: 'line' },
    reports: { type: 'line' },
    appointment: { type: 'line' },
    gender: { type: 'pie' },
    TAT: { type: 'line' },
    peerReview: { type: 'bar' },
    rollbackStatusCount: { type: 'bar' },
    rollbackReasonCount: { type: 'bar' },
    reportsPerRadiologist: { type: 'line' },
    avgReportingTime: { type: 'brushBar' },
    stratBussMetrix: { type: 'line' },
    radioCaseRatio: { type: 'bar' },
  });
  const [totalNumber, setTotalNumber] = useState({
    patient: '',
    reports: '',
    appointment: ''
  });

  const resetGraphData = () => {
    setGraphData({
      patientGraphData: [],
      reportGraphData: [],
      appointGraphData: [],
      genderGraphData: [],
      turnAroundTimeData: [],
      peerReviewData: [],
      rollbackStatusCounts: [],
      rollBackReasonCounts: [],
      radiologistPerformance: [],
      calculateAverageReportingTime: [],
      stratBussMetrix: [],
      radioCaseRatio: [],
    });
  }

  const generateColor = (index) => {
    // this function will generate the similar random color based on project theme
    const baseColor = COLORS[index % COLORS.length];
    const base = parseInt(baseColor.slice(1), 16);
    const r = Math.min(255, Math.max(0, (base >> 16) + Math.floor(Math.random() * 50) - 25));
    const g = Math.min(255, Math.max(0, ((base >> 8) & 0xff) + Math.floor(Math.random() * 50) - 25));
    const b = Math.min(255, Math.max(0, (base & 0xff) + Math.floor(Math.random() * 50) - 25));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const fetchCommonData = async () => {

    try {
      setIsloading(true);
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
      if (commonData?.status) {
        const modalityOptions = commonData?.result?.modality.map(modality => ({
          value: modality.value,
          label: modality.value,
        }));
        const othersIndex = modalityOptions.findIndex(option => option.value === "Others");

        // Insert "SM" before "Others"
        modalityOptions.splice(othersIndex, 0, {
          value: "SM",
          label: "SM"
        });

        setModalityOpton(modalityOptions);
      }
      setIsloading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const fetchDefaultData = async () => {
    try {
      const [
        patientsResponse,
        reportsResponse,
        appointmentsResponse,
        turnAroundTimeResponse,
        peerReviewResponse,
        rollbackErrorTrackingResponse,
        radiologistPerformanceResponse,
        calculateAverageReportingTimeResponse,
        stractBussiMetrixResponse,
        radioCaseRatioResponse,
      ] = await Promise.all([
        dispatch(getPatientAnalytics(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getReportAnalytics(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31&modality=${modality?.reports?.value || ''}`)).unwrap(),
        dispatch(getAppoitmentAnalytics(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31&modality=${modality?.appointments?.value || ''}`)).unwrap(),
        dispatch(getTurnAroundTime(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getPeerReview(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getRollbackErrorTracking(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getRadiologistPerformance(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(gatCalcAvgReportTime(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getStratBussiMatrix(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
        dispatch(getRadiologistCaseRatio(`?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31`)).unwrap(),
      ]);

      const genderDataArray = Object.entries(patientsResponse?.result?.genderCounts || {})
        .filter(([_, value]) => value > 0) // Only include entries where value > 0
        .map(([key, value]) => ({
          name: key,
          value,
        }));

      const TurnAroundArray = (turnAroundTimeResponse?.result?.aggregatedAnalytics || []).map((item) => ({
        modality: item.modality,
        averageTAT: parseFloat(item.averageTAT),
        count: item.count,
      }));

      setGraphData((prev) => ({
        ...prev,
        patientGraphData: patientsResponse?.result?.monthlyRegistrations || [],
        reportGraphData: reportsResponse?.result?.monthlyReports || [],
        appointGraphData: appointmentsResponse?.monthlyAppointments || [],
        genderGraphData: genderDataArray,
        turnAroundTimeData: TurnAroundArray || [],
        peerReviewData: peerReviewResponse?.data?.peerReviewedCases || [],
        rollbackStatusCounts: rollbackErrorTrackingResponse?.rollbackStatusCounts || [],
        rollBackReasonCounts: rollbackErrorTrackingResponse?.rollbackReasonCounts || [],
        radiologistPerformance: radiologistPerformanceResponse?.data || [],
        calculateAverageReportingTime: calculateAverageReportingTimeResponse?.cases || [],
        stratBussMetrix: stractBussiMetrixResponse?.geographicDistribution || [],
        radioCaseRatio: radioCaseRatioResponse?.data || [],
      }));
      setTotalNumber({
        patient: patientsResponse?.result?.totalPatients || 0,
        reports: reportsResponse?.result?.totalReports || 0,
        appointment: appointmentsResponse?.totalAppointments || 0,
      });

    } catch (error) {
      console.error('Error fetching default data:', error);
    }
  };

  const fetchFilteredData = async (type) => {
    try {
      if (type === 'reports') {
        const query = `?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31&modality=${modality?.reports?.value || ''}`
        const response = await dispatch(getReportAnalytics(query)).unwrap();
        setGraphData(prev => ({ ...prev, reportGraphData: response?.result.monthlyReports || [] }));
      } else if (type === 'appointments') {
        const query = `?startDate=${selectedYear}-01-01&endDate=${selectedYear}-12-31&modality=${modality?.appointments?.value || ''}`;
        const response = await dispatch(getAppoitmentAnalytics(query)).unwrap();
        setGraphData(prev => ({ ...prev, appointGraphData: response?.monthlyAppointments || [] }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    }
  };

  // Handle graph type change (toggle between line and bar graph)
  const handleChangeGraphType = (type, graphType) => {
    setGraphType(prevState => ({
      ...prevState,
      [type]: { type: graphType },
    }));
  };

  const genderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    // Calculate responsive font size based on outerRadius
    const fontSize = Math.max(10, Math.min(outerRadius / 10, 16)); // Between 10px and 16px

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: `${fontSize}px` }} // Apply responsive font size
      >
        {`${name[0]} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };


  useEffect(() => {
    resetGraphData();
    fetchCommonData();
    fetchDefaultData();
  }, [selectedYear]);



  const isFirstReportsRender = React.useRef(true);
  useEffect(() => {
    if (isFirstReportsRender.current) {
      isFirstReportsRender.current = false;
      return;
    }
    if (modality.reports === '') {
      fetchDefaultData();
    } else if (modality.reports) {
      fetchFilteredData('reports');
    }
  }, [modality.reports]);


  const isFirstAppointmentsRender = React.useRef(true);
  useEffect(() => {
    if (isFirstAppointmentsRender.current) {
      isFirstAppointmentsRender.current = false;
      return;
    }
    if (modality.appointments === '') {
      fetchDefaultData();
    } else if (modality.appointments) {
      fetchFilteredData('appointments');
    }
  }, [modality.appointments]);
  return (
    <div>
      {/* Executive Summary Row */}
      <div className='card-outer'>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-1' src={CameraBlue} alt='icon' />
            </div>
          </div>
          <div className='title title--h6 mt-2'>Total Patients</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{reportAnalyticsResponse?.result?.totalReports || '--'}</div>
            <div className='green-label'>{reportAnalyticsResponse?.result?.percentageIncrease || '--'}</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-2' src={Peoples} alt='icon' />
            </div>
          </div>
          <div className='title title--h6 mt-2'>Total Active Patient</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{patientAnalyticsResponse?.result?.totalPatients || '--'}</div>
            <div className='green-label'>{patientAnalyticsResponse?.result?.newCasesPercentage || '--'}</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-3' src={AddUser} alt='icon' />
            </div>
          </div>
          <div className='title title--h6 mt-2'>New Patient</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>{stractNBussiMetrixResponse?.result?.monthlyNewPatients || '--'}</div>
            <div className='green-label'>+10%</div>
          </div>
        </div>
        <div className='analytics-top-card-container'>
          <div>
            <div>
              <img className='icon-4' src={CutBox} alt='icon' />
            </div>
          </div>
          <div className='title title--h6 mt-2'>Patient Retention Rate</div>
          <div className='flex items-center mt-2 gap-2.5'>
            <div className='font-semibold text-2xl'>98%</div>
            <div className='green-label'>+10%</div>
          </div>
        </div>
      </div>
      {/* Operational Metrics Row */}
      <div className='analytics-card-container mt-4 mb-4'>
        <h3 className='title title--h4 mb-2'>Operational Metrics</h3>
        <div className='grid-container'>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Patients</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.patient.type === 'line'}
                    onChange={() => handleChangeGraphType('patient', 'line')}
                  />
                  <label className="form__radio__label">Line Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.patient.type === 'bar'}
                    onChange={() => handleChangeGraphType('patient', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData?.patientGraphData?.length === 0 ?
                (
                  <div style={{ height: '27rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>No Data Found to display chart..</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px", marginTop: '60px' }}>
                      {graphType.patient.type === 'line' ?
                        <ResponsiveContainer>
                          <LineChart data={graphData.patientGraphData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" padding={{ left: 30, right: 30 }}>
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Patients" angle={-90} offset={10} position="insideLeft" />
                            </YAxis>
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke={COLORS[0]} activeDot={{ r: 8 }} />
                          </LineChart>
                        </ResponsiveContainer>
                        :
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            width={500}
                            height={300}
                            data={graphData.patientGraphData}
                            margin={{
                              top: 5,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month">
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Patients" angle={-90} offset={10} position="insideLeft" />
                            </YAxis>
                            <Tooltip />
                            <Bar dataKey="count" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                          </BarChart>
                        </ResponsiveContainer>
                      }
                    </div>
                    <div className='flex justify-center'>
                      Total Patients: {totalNumber?.patient || '--'}
                    </div>
                  </>
                )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Reports</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                  type="radio"
                  value="line"
                  className='form__radio__input'
                  checked={graphType.reports.type === 'line'}
                  onChange={() => handleChangeGraphType('reports', 'line')}
                />
                  <label className="form__radio__label">Line Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                  type="radio"
                  value="bar"
                  className='form__radio__input'
                  checked={graphType.reports.type === 'bar'}
                  onChange={() => handleChangeGraphType('reports', 'bar')}
                />

                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              <div>
                <div className='modality-dropdown' style={{ width: "100%", marginTop: '10px', marginBottom: '10px' }}>
                  <Select
                    className="flex-2"
                    id="modality-wise"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable={true}
                    isDisabled={false}
                    isSearchable={true}
                    onChange={(value) => {
                      if (!value) {
                        setModality((prev) => ({ ...prev, reports: '' }));
                      } else {
                        setModality((prev) => ({ ...prev, reports: value }));
                      }
                    }}
                    options={_modalityOptions}
                    placeholder="Modality"
                    value={modality.reports}
                  />
                </div>
              </div>
              {graphData?.reportGraphData?.length === 0 ?
                (
                  <div style={{ height: '27rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>No Data Found to display chart..</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                      {graphType.reports.type == 'line' ?
                        <ResponsiveContainer>
                          <LineChart data={graphData.reportGraphData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" padding={{ left: 30, right: 30 }}>
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Reports" angle={-90} offset={10} position="insideLeft" />
                            </YAxis>
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke={COLORS[0]} activeDot={{ r: 8 }} />
                          </LineChart>
                        </ResponsiveContainer>
                        :
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            width={500}
                            height={300}
                            data={graphData.reportGraphData}
                            margin={{
                              top: 5,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month">
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Reports" angle={-90} offset={10} position="insideLeft" />
                            </YAxis>
                            <Tooltip />
                            <Bar dataKey="count" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                          </BarChart>
                        </ResponsiveContainer>
                      }
                    </div>
                    <div className='flex justify-center'>
                      Total Reports: {totalNumber?.reports || '--'}
                    </div>
                  </>
                )
              }
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Appointments</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                    <input
                      type="radio"
                      value="line"
                      className='form__radio__input'
                      checked={graphType.appointment.type === 'line'}
                      onChange={() => handleChangeGraphType('appointment', 'line')}
                    />
                    <label className="form__radio__label">Line Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.appointment.type === 'bar'}
                    onChange={() => handleChangeGraphType('appointment', 'bar')}
                    />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              <div>
                <div className='modality-dropdown' style={{ width: "100%", marginTop: '10px', marginBottom: '10px' }}>
                  <Select
                    className="flex-2"
                    id="modality-wise"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable={true}
                    isDisabled={false}
                    isSearchable={true}
                    onChange={(value) => {
                      if (!value) {
                        setModality((prev) => ({ ...prev, appointments: '' }));
                      } else {
                        setModality((prev) => ({ ...prev, appointments: value }));
                      }
                    }}
                    options={_modalityOptions}
                    placeholder="Modality"
                    value={modality.appointments}
                  />
                </div>
              </div>
              {graphData?.appointGraphData?.length === 0 ?
                (
                  <div style={{ height: '27rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>No Data Found to display chart..</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                      {graphType.appointment.type == 'line' ?
                        <ResponsiveContainer>
                          <LineChart data={graphData.appointGraphData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" padding={{ left: 30, right: 30 }}>
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Appointments" angle={-90} offset={20} position="insideBottomLeft" />
                            </YAxis>
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke={COLORS[0]} activeDot={{ r: 8 }} />
                          </LineChart>
                        </ResponsiveContainer>
                        :
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            width={500}
                            height={300}
                            data={graphData.appointGraphData}
                            margin={{
                              top: 5,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month">
                              <Label value="Months" offset={-5} position="insideBottom" />
                            </XAxis>
                            <YAxis>
                              <Label value="Total Appointments" angle={-90} offset={10} position="insideBottomLeft" />
                            </YAxis>
                            <Tooltip />
                            <Bar dataKey="count" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                          </BarChart>
                        </ResponsiveContainer>
                      }
                    </div>
                    <div className='flex justify-center'>
                      Total Appointments: {totalNumber?.appointment || '--'}
                    </div>
                  </>
                )
              }
            </div>
          </div>
        </div>
        <div className='grid-2-container'>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Patient Gender</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.gender.type === 'pie'}
                    onChange={() => handleChangeGraphType('gender', 'pie')}
                  />
                  <label className="form__radio__label">Pie Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                  type="radio"
                  value="bar"
                  className='form__radio__input'
                  checked={graphType.gender.type == 'bar'}
                  onChange={() => handleChangeGraphType('gender', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData?.genderGraphData?.length === 0 ?
                (
                  <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>No Data Found to display chart..</p>
                  </div>
                ) : (
                  graphType.gender.type === 'pie' ?
                    <ResponsiveContainer width="90%" height={400}>
                      <PieChart>
                        <Pie
                          data={graphData.genderGraphData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={genderLabel}
                          outerRadius={150}
                          fill={COLORS[0]}
                          dataKey="value"
                        >
                          {graphData?.genderGraphData?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || generateColor(index)} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    :
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        width={500}
                        height={300}
                        data={graphData.genderGraphData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name">
                          <Label value="Gender" offset={-5} position="insideBottom" />
                        </XAxis>
                        <YAxis>
                          <Label value="Count" angle={-90} offset={10} position="insideLeft" />
                        </YAxis>
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" fill={COLORS[1]} activeBar={<Rectangle fill={COLORS[2]} stroke={COLORS[1]} />} />
                      </BarChart>
                    </ResponsiveContainer>
                )}
              <div className='flex justify-center space-x-4'>
                {graphData?.genderGraphData.map((data, index) => {
                  return <span className='flex items-center gap-2'>{data?.name}: {data?.value}</span>
                })}
              </div>
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Turn Around Time (TAT)</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.TAT.type === 'line'}
                    onChange={() => handleChangeGraphType('TAT', 'line')}
                  />
                  <label className="form__radio__label">Scatter Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.TAT.type == 'bar'}
                    onChange={() => handleChangeGraphType('TAT', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData?.turnAroundTimeData?.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (graphType?.TAT?.type === 'line' ?
                <ResponsiveContainer width="100%" height={400}>
                  <ScatterChart
                    margin={{
                      top: 20,
                      right: 20,
                      bottom: 20,
                      left: 20,
                    }}
                  >
                    <CartesianGrid />
                    <XAxis type="number" dataKey="averageTAT" name="Average TAT" unit="hrs" padding={{ left: 30, right: 30 }} >
                      <Label value="Average TAT" offset={-20} position="insideBottom" />
                    </XAxis>
                    <YAxis type="number" dataKey="count" name="Count" >
                      <Label value="Count" angle={-90} offset={12} position="insideLeft" />
                    </YAxis>
                    <Tooltip content={<CustomTooltipTAT />} cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter name="Modalities" data={graphData.turnAroundTimeData} fill={COLORS[0]} />
                  </ScatterChart>
                </ResponsiveContainer>
                :
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={graphData.turnAroundTimeData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="modality" >
                      <Label value="Average TAT" offset={-5} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Count" angle={-90} offset={12} position="insideLeft" />
                    </YAxis>
                    <Tooltip />

                      <Bar dataKey="averageTAT" stackId="a" fill={COLORS[1]} />
                    <Bar dataKey="count" stackId="a" fill={COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Quality Metrics Row */}
      <div className='analytics-card-container mt-4 mb-4'>
        <h3 className='title title--h4 mb-2'>Quality Metrics</h3>
        <div className='grid-container'>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Peer Review Cases</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.peerReview.type === 'bar'}
                    onChange={() => handleChangeGraphType('peerReview', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="pie"
                    className='form__radio__input'
                    checked={graphType.peerReview.type == 'pie'}
                    onChange={() => handleChangeGraphType('peerReview', 'pie')}
                    />
                  <label className="form__radio__label">Pie Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.peerReviewData.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.peerReview.type === 'bar' ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={graphData.peerReviewData}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 30,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name">
                        <Label value="Users" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label value="Total Reports" angle={-90} offset={10} position="insideLeft" />
                      </YAxis>
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          dataKey="count"
                          isAnimationActive={false}
                          data={graphData.peerReviewData}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill={COLORS[0]}
                          label={(entry) => `${entry.name}: ${entry.count}`}
                        />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Rollback Status Counts</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.rollbackStatusCount.type === 'bar'}
                    onChange={() => handleChangeGraphType('rollbackStatusCount', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="pie"
                    className='form__radio__input'
                    checked={graphType.rollbackStatusCount.type == 'pie'}
                    onChange={() => handleChangeGraphType('rollbackStatusCount', 'pie')}
                  />
                  <label className="form__radio__label">Pie Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.rollbackStatusCounts.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.rollbackStatusCount.type === 'bar' ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={graphData.rollbackStatusCounts}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 30,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name">
                        <Label value="Status" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label value="Total Reports" angle={-90} offset={10} position="insideLeft" />
                      </YAxis>
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          dataKey="count"
                          isAnimationActive={false}
                          data={graphData.rollbackStatusCounts}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill={COLORS[0]}
                          label={(entry) => `${entry.name}: ${entry.count}`}
                        >
                          {graphData.rollbackStatusCounts.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || generateColor(index)} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Rollback Reason Counts</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.rollbackReasonCount.type === 'bar'}
                    onChange={() => handleChangeGraphType('rollbackReasonCount', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="pie"
                    className='form__radio__input'
                    checked={graphType.rollbackReasonCount.type == 'pie'}
                    onChange={() => handleChangeGraphType('rollbackReasonCount', 'pie')}
                  />
                  <label className="form__radio__label">Pie Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.rollBackReasonCounts.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.rollbackReasonCount.type === 'bar' ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={graphData.rollBackReasonCounts}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 30,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name">
                        <Label value="Correction Reason" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label value="Total Reports" angle={-90} offset={10} position="insideLeft" />
                      </YAxis>
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          dataKey="count"
                          isAnimationActive={false}
                          data={graphData.rollBackReasonCounts}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill={COLORS[0]}
                          label={(entry) => `${entry.name}: ${entry.count}`}
                        >
                          {graphData.rollBackReasonCounts.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || generateColor(index)} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Radiologist Performace */}
      <div className='analytics-card-container mt-4 mb-4'>
        <h3 className='title title--h4 mb-2'>Radiologist Performance</h3>
        <div className='grid-2-container' style={{ paddingTop: '0' }}>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Number of Reports per Radiologist</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.reportsPerRadiologist.type === 'line'}
                    onChange={() => handleChangeGraphType('reportsPerRadiologist', 'line')}
                  />
                  <label className="form__radio__label">Line Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="pie"
                    className='form__radio__input'
                    checked={graphType.reportsPerRadiologist.type == 'pie'}
                    onChange={() => handleChangeGraphType('reportsPerRadiologist', 'pie')}
                  />
                  <label className="form__radio__label">Pie Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.radiologistPerformance.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.reportsPerRadiologist.type === 'line' ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={graphData.radiologistPerformance} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" padding={{ left: 30, right: 30 }}>
                        <Label value="Radiologists" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label
                          value="Reports"
                          angle={-90}
                          offset={10}
                          position="insideLeft"
                        />
                      </YAxis>
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={COLORS[0]}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={500}>
                      <PieChart>
                        <Pie
                          dataKey="count"
                          isAnimationActive={false}
                          data={graphData.radiologistPerformance}
                          cx="50%"
                          cy="50%"
                          outerRadius={140}
                          fill={COLORS[0]}
                          label={(entry) => `${entry.name}: ${entry.count}`}
                        />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Average reporting time per case</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="brushBar"
                    className='form__radio__input'
                    checked={graphType.avgReportingTime.type === 'brushBar'}
                    onChange={() => handleChangeGraphType('avgReportingTime', 'brushBar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.avgReportingTime.type == 'line'}
                    onChange={() => handleChangeGraphType('avgReportingTime', 'line')}
                  />
                  <label className="form__radio__label">Line Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.calculateAverageReportingTime.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.avgReportingTime.type === 'brushBar' ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      width={800}
                      height={400}
                      data={graphData.calculateAverageReportingTime}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="index" interval={0} textAnchor="end">
                        <Label value="Reports" offset={10} position="insideBottom" />
                      </XAxis>
                      <YAxis
                        tick={false}
                        label={{
                          value: 'Time (seconds)',
                          angle: -90,
                          position: 'center',
                        }}
                      />
                      <Tooltip />
                      <Legend verticalAlign="top" wrapperStyle={{ lineHeight: '40px' }} />
                      <ReferenceLine y={0} stroke={COLORS[0]} />
                      <Brush
                        dataKey="index"
                        height={40}
                        stroke="#8884d8"
                        startIndex={0}
                        endIndex={Math.min(29, graphData?.calculateAverageReportingTime?.length - 1)}
                        travellerWidth={10}
                      />
                      <Bar dataKey="totalSeconds" fill={COLORS[1]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={500}>
                      <LineChart data={graphData?.calculateAverageReportingTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="index" padding={{ left: 30, right: 30 }}>
                          <Label value="Reports" offset={-5} position="insideBottom" />
                        </XAxis>
                        <YAxis tick={false}>
                          <Label
                            value="Time (in seconds)"
                            angle={-90}
                            offset={10}
                            position="center"
                          />
                        </YAxis>
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="totalSeconds"
                          stroke={COLORS[0]}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Strategic and Business Metrics*/}
      <div className='analytics-card-container mt-4 mb-4'>
        <h3 className='title title--h4 mb-2'>Strategic and Business Metrics</h3>
        <div className='grid-2-container' style={{ paddingTop: '0' }}>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Geographic distribution of patient</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.stratBussMetrix.type === 'line'}
                    onChange={() => handleChangeGraphType('stratBussMetrix', 'line')}
                  />
                  <label className="form__radio__label">Line Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.stratBussMetrix.type == 'bar'}
                    onChange={() => handleChangeGraphType('stratBussMetrix', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.stratBussMetrix.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.stratBussMetrix.type === 'line' ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={graphData.stratBussMetrix} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" padding={{ left: 30, right: 30 }}>
                        <Label value="Places" offset={-20} position="insideBottom" style={{ paddingTop: '10px' }} />
                      </XAxis>
                      <YAxis>
                        <Label
                          value="Patients"
                          angle={-90}
                          offset={10}
                          position="insideLeft"
                        />
                      </YAxis>
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={COLORS[0]}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={graphData.stratBussMetrix}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 30,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name">
                        <Label value="Places" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label value="Patients" angle={-90} offset={10} position="insideLeft" />
                      </YAxis>
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Workforce Management */}
      <div className='analytics-card-container mt-4 mb-4'>
        <h3 className='title title--h4 mb-2'>Workforce Management</h3>
        <div className='grid-2-container' style={{ paddingTop: '0' }}>
          <div className="grid-item analytics-card-container">
            <div className='flex justify-between flex-wrap align-item-center gap-2'>
              <h3 className='title title--h5'>Radiologist to case ratio</h3>
              <div className='form__radio form__radio--gray'>
                <div className="form__radio__option mb-0">
                  <input
                    type="radio"
                    value="bar"
                    className='form__radio__input'
                    checked={graphType.radioCaseRatio.type === 'bar'}
                    onChange={() => handleChangeGraphType('radioCaseRatio', 'bar')}
                  />
                  <label className="form__radio__label">Bar Graph</label>
                </div>
                <div className="form__radio__option mb-0 mr-0">
                  <input
                    type="radio"
                    value="line"
                    className='form__radio__input'
                    checked={graphType.radioCaseRatio.type == 'line'}
                    onChange={() => handleChangeGraphType('radioCaseRatio', 'line')}
                  />
                  <label className="form__radio__label">Line Graph</label>
                </div>
              </div>
            </div>
            <div>
              {graphData.radioCaseRatio.length === 0 ? (
                <div style={{ height: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <p>No Data Found to display chart..</p>
                </div>
              ) : (
                graphType.radioCaseRatio.type === 'bar' ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={graphData.radioCaseRatio}
                      margin={{
                        top: 20,
                        right: 20,
                        left: 20,
                        bottom: 30,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="radiologist">
                        <Label value="Radiologists" offset={-15} position="insideBottom" />
                      </XAxis>
                      <YAxis>
                        <Label value="Ratio & Cases" angle={-90} offset={10} position="insideLeft" />
                      </YAxis>
                      <Tooltip />
                      <Bar dataKey="caseCount" fill={COLORS[1]} />
                      <Bar dataKey="ratio" fill={COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={graphData.radioCaseRatio} margin={{ top: 20, right: 20, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="radiologist" padding={{ left: 30, right: 30 }}>
                        <Label value="Radiologists" offset={-20} position="insideBottom" style={{ paddingTop: '10px' }} />
                      </XAxis>
                      <YAxis>
                        <Label
                          value="Ratio & Cases"
                          angle={-90}
                          offset={10}
                          position="insideLeft"
                        />
                      </YAxis>
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey={COLORS[1]}
                        stroke="#82ca9d"
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="ratio"
                        stroke={COLORS[0]}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={isloading} />
    </div>
  )
}

export default AdminGraph
